// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useRef, useState } from "react";
import { ready, unlockWithPassphrase, unlockWithRecoveryKey, type KeyBundle, type UnlockedKeys } from "@almanac/crypto";
import { api } from "../lib/api.js";
import { useI18n } from "../lib/i18n.js";

export function Unlock({ notice, onUnlocked, onSignOut }: { notice: string | null; onUnlocked: (keys: UnlockedKeys) => void; onSignOut: () => void }) {
  const { t } = useI18n();
  const [bundle, setBundle] = useState<KeyBundle | null>(null);
  const [secret, setSecret] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.getKeys().then(
      (r) => setBundle(r.bundle),
      (err: Error) => setError(t("common.error", { message: err.message })),
    );
  }, [t]);

  useEffect(() => input.current?.focus(), [useRecovery]);

  return (
    <div className="auth">
      <main className="card" id="main">
        <h1>{t("unlock.title")}</h1>
        <p>{t("unlock.intro")}</p>
        {notice && <p className="hint">{notice}</p>}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!bundle) return;
            setWorking(true);
            setError(null);
            try {
              await ready();
              await new Promise((r) => setTimeout(r, 30));
              onUnlocked(useRecovery ? unlockWithRecoveryKey(bundle, secret) : unlockWithPassphrase(bundle, secret));
            } catch {
              setError(t("unlock.wrong"));
              setWorking(false);
            }
          }}
        >
          <div className="field">
            <label htmlFor="unlock-secret">{useRecovery ? t("unlock.recoveryKey") : t("unlock.passphrase")}</label>
            <input
              ref={input}
              id="unlock-secret"
              type={useRecovery ? "text" : "password"}
              autoComplete={useRecovery ? "off" : "current-password"}
              spellCheck={false}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "unlock-error" : undefined}
              required
            />
          </div>
          {error && (
            <p role="alert" id="unlock-error" className="form-error">
              {error}
            </p>
          )}
          <div className="row">
            <button className="primary" type="submit" disabled={working || !bundle}>
              {working ? t("unlock.working") : t("unlock.submit")}
            </button>
            <button
              type="button"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setSecret("");
                setError(null);
              }}
            >
              {useRecovery ? t("unlock.usePassphrase") : t("unlock.useRecovery")}
            </button>
            <button type="button" onClick={onSignOut}>
              {t("auth.signOut")}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
